// The A and H marks: which card has had its orders copied, per side, for this session and no longer.
//
// R-I / R8: Maven does not survive a logout. Nothing about a run is saved — not the inputs, not the
// cards, not the ranking, not the selection, and not these marks. The only thing that persists is
// the transaction the trader actually sent. So the marks need no saved identity and no store: they
// live here, in memory, and they are gone when the panel is.
//
// There is deliberately NO persistence call anywhere in this file. If one ever appears, it is a
// defect — a mark restored at login would claim an order was sent for a run that no longer exists,
// against prices that have since moved.
//
// One piece of state, two readers (criterion 19B): the card's marks and the Details column's order
// buttons both read this, so they cannot disagree about what has been copied and from where.
//
// ONE APPLIED CARD (owner, vrequest + rulings 2026-09-15, CR-20260915-003). A card is APPLIED as
// soon as ANY half of A or H is lit on it. While a card is applied, every other card's order
// buttons are read-only — the trader can still select them and read their Summary, but cannot open
// a pop-up from them. This REPLACES the 2026-09-13 "a copy from another card overrides" rule: the
// other card cannot copy at all. The lock releases only when the saved state behind every lit half
// is cleared where it lives (the APG for A, the Hedging Grid for H).
//
// PER SIDE (same ruling). Each mark has a Calls half and a Puts half. The two pop-ups copy one side
// at a time, so "A is lit" was ambiguous: it said neither which side nor whether both. The halves
// say it. On a single-side portfolio the one copy lights both halves — the whole chip.

import {Injectable, OnDestroy} from '@angular/core';
import {BehaviorSubject, Observable, Subscription} from 'rxjs';
import {MessageBusService} from '../../message-bus.service';
import {ApgPortfolio} from '../../adjustment-pricing-grid/model/ApgPortfolio';
import {CashFlowAdjustment} from '../../adjustment-pricing-grid/model/CashFlowAdjustment';
import {
    APG_AFTER_STATE_UPDATED_TOPIC,
    ApgAfterStateUpdatedPayload,
    PositionsAfterStateService
} from '../../adjustment-pricing-grid/services/positions-after-state.service';
import {HedgePositionsService} from '../../hedging-grid/positions-section/hedge-positions/hedge-positions.service';
import {MavenCard} from './maven-delivery.contract';

/** Which half of a package an order covers. The two transactions are never merged (G11). */
export type MavenOrderPart = 'adjustment' | 'hedges';

/**
 * The slot a pop-up saved into. BOTH pop-ups write 'first' for Calls and 'second' for Puts on a
 * Calls & Puts portfolio, and always 'first' on a single-side one (adjustment-solution-popup
 * .component.ts:444-447, hg-transaction-popup.component.ts:505-511). Maven reads the slot; it never
 * invents the side.
 */
export type MavenSaveSlot = 'first' | 'second';

/** The two halves of one mark: the Calls half and the Puts half. */
export interface MavenSideMarks {
    calls: boolean;
    puts: boolean;
}

export interface MavenAppliedMarks {
    adjustment: MavenSideMarks;
    hedges: MavenSideMarks;
}

const NO_SIDES: MavenSideMarks = {calls: false, puts: false};

const NOTHING_APPLIED: MavenAppliedMarks = {adjustment: NO_SIDES, hedges: NO_SIDES};

/** True when any half of either mark is lit. */
export function isAnyMarkLit(marks: MavenAppliedMarks): boolean {
    return !!marks && (
        marks.adjustment.calls || marks.adjustment.puts || marks.hedges.calls || marks.hedges.puts
    );
}

/**
 * Identity of a card WITHIN one run.
 *
 * Date plus variant, not rank: a manual re-sort leaves ranks alone, but a card's identity must not
 * depend on a display concept at all. Several cards can share a date — one per hedge pair the
 * per-expiration cap let through (CR-20260921-003) — and they differ by variant ("c1p1", "c1p2",
 * the ORION's ranks of the pair), so the pair is unique, and marking one never marks another
 * (criterion 5D).
 *
 * This key lives and dies with the run. It is never written anywhere.
 */
export function mavenCardKey(card: MavenCard): string {
    return `${card.date}|${card.variant}`;
}

/** The hedge pop-up echoes back the identity its caller supplied, and the slot it saved, on its save. */
export const HG_SAVE_AFTER_STATE_TOPIC = 'Hg.SaveAfterState';

/** Published after EVERY write to a portfolio's hedge after-state — a save as well as a clear. */
export const HG_AFTER_STATE_UPDATED_TOPIC = 'Hg.AfterStateUpdated';

/** The adjustment pop-up's save, echoing the caller's own identity and the slot it saved into. */
export const ADJUSTMENT_AFTER_STATE_TOPIC = 'Pkg-Cmprsn.AfterStateSelected';

/** What a save message carries that Maven reads. Both topics share this shape. */
interface SaveEcho {
    packageId?: string;
    slot?: MavenSaveSlot;
}

/** Which slots the trader copied, per part, on the applied card. */
interface CopiedSlots {
    first: boolean;
    second: boolean;
}

@Injectable()
export class MavenAppliedMarksService implements OnDestroy {

    private readonly _changed = new BehaviorSubject<void>(undefined);

    private _busSubscriptions: Subscription[] = [];

    /** The cards of the current run, and the run facts needed to find their saved state. */
    private _cards: MavenCard[] = [];
    private _portfolio: ApgPortfolio = null;
    private _portfolioId: string = null;
    private _strategyType: string = null;

    /**
     * THE ONE APPLIED CARD — its key, and which slots the trader copied from it, per part.
     *
     * THIS IS THE ONE AUTHOR OF "WHICH CARD" AND "WHICH SIDE". It is written only when a pop-up
     * echoes back the id Maven gave it plus the slot it saved, which is the only signal that says
     * WHO saved WHAT. The stored state is then asked one question and one only — is that slot still
     * holding what was saved — because it cannot answer the identity question and must never be
     * made to appear to.
     *
     * The marks a card shows are DERIVED from this and the stores on every read (marksFor), never
     * cached: a cache is a second author.
     */
    private _appliedKey: string | null = null;
    private _copiedAdjustment: CopiedSlots = {first: false, second: false};
    private _copiedHedges: CopiedSlots = {first: false, second: false};

    constructor(
        private readonly _messageBus: MessageBusService,
        private readonly _positionsAfterState: PositionsAfterStateService,
        private readonly _hedgePositions: HedgePositionsService
    ) {

        // A MARK IS A VIEW OF THE SAVED STATE, not a flag this panel sets when it opens a dialog
        // (owner ruling, 2026-09-11: "hitting a copy means I have selected this ... when we remove
        // the saved state ... we must remove the mark on the MAVEN card also").
        //
        // The route is the one Package Comparison already uses, not a new channel: the caller hands
        // the pop-up an identity as `pkgId`, the pop-up echoes it back on its save, and the caller
        // matches it to one of its own cards. Maven's identity is the card key. The slot rides on
        // the same message and names the side.
        this._busSubscriptions.push(
            this._messageBus
                .of<SaveEcho>(HG_SAVE_AFTER_STATE_TOPIC)
                .subscribe(msg => this.onSaved('hedges', msg.payload))
        );

        // ⚠ THIS TOPIC FOLLOWS A SAVE AS WELL AS A CLEAR. The hedge pop-up publishes its save message
        // (sync, above); the Hedging Grid then persists it and publishes THIS one, async. Treating
        // it as "cleared" put the H mark out a tick after every copy — the mark the trader saw
        // flicker (vrequest 2026-09-13). So it is a signal to RE-READ presence: a half goes out only
        // when its slot no longer holds a saved hedge after-state.
        this._busSubscriptions.push(
            this._messageBus
                .of<{ portfolio?: ApgPortfolio }>(HG_AFTER_STATE_UPDATED_TOPIC)
                .subscribe(msg => this.onHedgeAfterStateUpdated(msg.payload))
        );

        // THE ADJUSTMENT SAVE, carrying the card key Maven handed the pop-up and the slot it wrote.
        //
        // A save with no package id came from the APG or somewhere else and is not Maven's to mark.
        // That is the whole reason the id exists: neither this message nor afterStateUpdated$ ever
        // said WHO saved, so "the card Maven last opened" plus "a save happened somewhere" would
        // light a Maven mark when the trader saved in the APG.
        this._busSubscriptions.push(
            this._messageBus
                .of<SaveEcho>(ADJUSTMENT_AFTER_STATE_TOPIC)
                .subscribe(msg => this.onSaved('adjustment', msg.payload))
        );

        // Any change to the saved adjustment state — a save, an overwrite, or a CLEAR from the APG.
        // Presence is re-read from the state itself; identity is never taken from here.
        //
        // ⚠ ON THE BUS, NOT ON `_positionsAfterState.afterStateUpdated$`. That event belongs to an
        // INSTANCE, and this panel holds its own instance of the service (maven-panel.component.ts,
        // providers) — so the APG's clear, emitted on the root instance, never reached here and the
        // A mark stayed lit over an empty slot (vrequest 2026-09-13). The store is shared; only the
        // notification was not. The bus topic is published by every instance on every write.
        this._busSubscriptions.push(
            this._messageBus
                .of<ApgAfterStateUpdatedPayload>(APG_AFTER_STATE_UPDATED_TOPIC)
                .subscribe(() => this.onStoreChanged())
        );
    }

    /**
     * Take over a run: the cards, and the facts needed to locate their saved state.
     * Called when a delivery lands and when the portfolio changes.
     */
    attachRun(cards: MavenCard[], portfolio: ApgPortfolio, strategyType: string): void {
        this._cards = cards || [];
        this._portfolio = portfolio || null;
        this._portfolioId = portfolio ? portfolio.id : null;
        this._strategyType = strategyType;
        this.onStoreChanged();
    }

    ngOnDestroy(): void {
        this._busSubscriptions.forEach(s => s.unsubscribe());
        this._busSubscriptions = [];
    }

    /** Emits whenever a mark may have changed or the store is reset. */
    get changed(): Observable<void> {
        return this._changed.asObservable();
    }

    /**
     * The key of the applied card — the one card with any half lit — or null when no card is.
     *
     * Derived, not stored: the recorded card stays recorded until a new run, but it is APPLIED only
     * while a lit half still has saved state behind it. So a full clear in the APG and the Hedging
     * Grid releases the lock on its own, with nothing needing to tell Maven.
     */
    get appliedCardKey(): string | null {

        if (this._appliedKey === null) {
            return null;
        }

        return isAnyMarkLit(this.marksForKey(this._appliedKey)) ? this._appliedKey : null;
    }

    /**
     * The marks for a card. Never null — a card nobody has copied from reads as four unlit halves.
     */
    marksFor(card: MavenCard): MavenAppliedMarks {
        if (!card) {
            return NOTHING_APPLIED;
        }

        return this.marksForKey(mavenCardKey(card));
    }

    /**
     * Forget everything. Called when a new run's delivery arrives and when the panel is destroyed.
     *
     * A new run produces new packages at new prices; carrying a mark across would attach a sent
     * order to a package that no longer exists.
     */
    reset(): void {
        const hadSomething = this._appliedKey !== null;

        this._appliedKey = null;
        this._copiedAdjustment = {first: false, second: false};
        this._copiedHedges = {first: false, second: false};

        if (hadSomething) {
            this._changed.next();
        }
    }

    /**
     * A pop-up echoed Maven's own card key and the slot it saved into.
     *
     * The buttons of every other card are disabled while a card is applied (Details column), so a
     * save for a DIFFERENT key can only arrive when nothing is applied any more — the previous
     * card's halves have all gone out. Then the new card simply takes over. If it ever arrived
     * otherwise, the store WAS overwritten by that save, so taking over is still the true picture;
     * the previous card's recorded slots are dropped rather than left to claim state that is gone.
     */
    private onSaved(part: MavenOrderPart, payload: SaveEcho | null): void {

        const key = payload ? payload.packageId : null;
        const slot = payload ? payload.slot : null;

        if (!key || (slot !== 'first' && slot !== 'second')) {
            return;
        }

        if (this._appliedKey !== key) {
            this._appliedKey = key;
            this._copiedAdjustment = {first: false, second: false};
            this._copiedHedges = {first: false, second: false};
        }

        const copied = part === 'adjustment' ? this._copiedAdjustment : this._copiedHedges;
        copied[slot] = true;

        this._changed.next();
    }

    /**
     * The saved hedge after-state of some portfolio changed — saved, adjusted or cleared. Only the
     * run's own portfolio matters; then presence is re-read, never inferred from the message kind.
     */
    private onHedgeAfterStateUpdated(payload: { portfolio?: ApgPortfolio } | null): void {

        const portfolio = payload ? payload.portfolio : null;

        if (!portfolio || !this._portfolioId || portfolio.id !== this._portfolioId) {
            return;
        }

        this.onStoreChanged();
    }

    /** A store this service derives from was written. Readers re-derive; nothing is cached here. */
    private onStoreChanged(): void {
        this._changed.next();
    }

    /**
     * Derive one card's four halves: a half is lit iff the trader copied THAT slot from THAT card
     * AND the slot still holds what was saved. Any other card reads unlit.
     *
     * Single-side portfolio: the pop-ups always save into 'first', and the one copy lights the whole
     * chip (owner, 2026-09-15) — so 'first' feeds both halves.
     */
    private marksForKey(key: string): MavenAppliedMarks {

        if (this._appliedKey !== key) {
            return NOTHING_APPLIED;
        }

        const card = this._cards.filter(c => c && mavenCardKey(c) === key)[0];

        if (!card) {
            return NOTHING_APPLIED;
        }

        const adjustmentFirst = this._copiedAdjustment.first && this.isAdjustmentStillSaved(card, 'first');
        const adjustmentSecond = this._copiedAdjustment.second && this.isAdjustmentStillSaved(card, 'second');
        const hedgesFirst = this._copiedHedges.first && this.isHedgeAfterStateSaved('first');
        const hedgesSecond = this._copiedHedges.second && this.isHedgeAfterStateSaved('second');

        if (this._strategyType !== 'double') {
            return {
                adjustment: {calls: adjustmentFirst, puts: adjustmentFirst},
                hedges: {calls: hedgesFirst, puts: hedgesFirst}
            };
        }

        return {
            adjustment: {calls: adjustmentFirst, puts: adjustmentSecond},
            hedges: {calls: hedgesFirst, puts: hedgesSecond}
        };
    }

    /**
     * Is one of this card's adjustments still sitting in the given saved slot?
     *
     * THE SINGLE-DATE CASE: two cards of the same day share that day's one adjustment, so they share
     * a descriptor. The descriptor alone never lights anything — only the recorded card is asked.
     */
    private isAdjustmentStillSaved(card: MavenCard, slot: MavenSaveSlot): boolean {

        const adjustments = (card.adjustment ? card.adjustment.adjustments : null) || [];

        for (const adjustment of adjustments) {

            if (!adjustment) {
                continue;
            }

            // The same three parts the pop-up builds its storage key from
            // (adjustment-solution-popup.component.ts:436-442), so this reads the state that save
            // actually wrote rather than a key of Maven's own invention.
            const strategy = this._strategyType === 'double'
                ? 'Calls & Puts'
                : adjustment.cashFlowStrategy;

            const state = this._positionsAfterState.getState(
                this._portfolioId, adjustment.underlying, strategy
            );

            if (!state) {
                continue;
            }

            const saved = slot === 'first' ? state.first : state.second;

            if (saved && saved.adjustmentDescriptor === makeAdjustmentDescriptor(adjustment)) {
                return true;
            }
        }

        return false;
    }

    /** Does the run's portfolio hold a saved hedge after-state in the given slot? */
    private isHedgeAfterStateSaved(slot: MavenSaveSlot): boolean {

        if (!this._portfolio) {
            return false;
        }

        const saved = this._hedgePositions.getAfterState(slot, this._portfolio);

        return !!saved && saved.length > 0;
    }
}


/**
 * The descriptor the save writes into a slot: the pretty date, then the logical id in parentheses.
 *
 * Built exactly as PositionsAfterStateService.saveState builds it (:59-63) — the date is the FIRST
 * token of the pretty expiration, and 'N/A' when there is none. It is a PRESENCE probe only: two
 * cards of one day share an adjustment and therefore share this string, so it is never used to
 * decide which card a save belonged to.
 *
 * ⚠ The pretty expiration is a FRONTEND-ONLY field, filled for Maven's cards at the delivery
 * boundary (maven-run-client.ts, restoreAdjustmentPrettyDates). Left unfilled, every card of the run
 * read 'N/A (<logicalId>)' here, and a copy from the second card could not put the first card out.
 */
export function makeAdjustmentDescriptor(adjustment: CashFlowAdjustment): string {

    const pretty = adjustment.expirationDatePrettyWithDaysToExpiration;
    const date = (pretty === null || pretty === undefined || pretty === '')
        ? 'N/A'
        : pretty.split(' ')[0];

    return date + ' (' + adjustment.logicalId + ')';
}
